import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useLanguage } from '../contexts/LanguageContext';
import { api, fetchWithAuth } from '../config/api';
import { dataCache } from '../utils/cache';
import './MapComponent.css';

// ============================================================================
// MapComponentGL — mismo contrato de props que MapComponent (Leaflet), pero
// renderizado por GPU con MapLibre GL (la arquitectura del mapa DFW).
// Los datos viajan como GeoJSON sources y el estilo es data-driven: no se crea
// un objeto por feature, la GPU pinta 10k+ puntos y polígonos a 60fps.
// Rollback: cambiar el import en App.js de MapComponentGL a MapComponent.
// ============================================================================

// Estilo vectorial Carto Positron (equivalente GL de los tiles raster light_all)
const POSITRON_GL_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

// Vista general de EE.UU. — GL usa ~1 nivel de zoom menos que Leaflet para el mismo encuadre
const US_CENTER = [-98.5795, 39.8283];
const US_ZOOM_GL = 3.2;
const GL_TO_LEAFLET_ZOOM = 1; // zoom reportado a App = glZoom + 1 (los límites de App están afinados a Leaflet)

const stateToAbbreviation = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Colores por certificación (mismo mapa que el componente Leaflet de certainteed)
const APOLLO_COLOR_MATCH = ['match', ['coalesce', ['get', 'color'], ''],
  'morado', '#6f42c1', 'azul-oscuro', '#003d82', 'verde', '#28a745',
  'naranja-oscuro', '#ff8c00', 'gris', '#6c757d', 'rosa-fucsia', '#e91e63',
  'rojo', '#dc3545', '#007bff'];
const APOLLO_COLOR_LABELS = {
  'azul': 'Certified', 'morado': 'Not certified', 'azul-oscuro': 'SSM', 'verde': 'SM',
  'naranja-oscuro': 'STAR', 'gris': 'SMARTCOAT', 'rosa-fucsia': '5 Star', 'rojo': 'Silver Star Siding'
};

// Clave primitiva para usar city en arrays de dependencias: un array nuevo
// en cada render dispararia los efectos en bucle.
const cityKey = (city) => (Array.isArray(city) ? city.join(',') : city || '');

const MapComponentGL = ({ data, filters, onBoundsChange, loading, layerVisibility, hispanicConcentration, tradeAreaLoading, onTradeAreaLoadingChange, isTopHispanicActive = false, topHispanicTradeAreas = [], apolloDbas = [], contractorFilters = null, addToast = () => {} }) => {
  const { t, getTranslations } = useLanguage();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [legendOpen, setLegendOpen] = useState(true);
  const [tradeAreaCount, setTradeAreaCount] = useState(0);
  const [detailPanel, setDetailPanel] = useState(null);
  const [zoomDisplay, setZoomDisplay] = useState(4);
  const [certainteedPoints, setCertainteedPoints] = useState(null);
  const [apolloPoints, setApolloPoints] = useState(null);
  const zipFeaturesRef = useRef(new Map());        // acumulado de zips (key=zipcode) entre pans
  const hoverPopupRef = useRef(null);               // tooltip flotante compartido
  const clickPopupRef = useRef(null);
  const selectedTaRef = useRef(null);               // id de trade area seleccionada (feature-state)
  const hoveredRef = useRef({});                    // {sourceId: featureId} para hover feature-state
  const onBoundsChangeRef = useRef(onBoundsChange);
  useEffect(() => { onBoundsChangeRef.current = onBoundsChange; });
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; });

  const formatNumber = (num) => (num || num === 0) ? new Intl.NumberFormat('en-US').format(num) : 'N/A';

  // ------------------------------------------------------------------ helpers
  const leafletZoom = () => Math.round((mapInstance.current?.getZoom() ?? US_ZOOM_GL)) + GL_TO_LEAFLET_ZOOM;

  const boundsForApp = () => {
    const b = mapInstance.current.getBounds();
    return { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
  };

  const setSourceData = (id, fc) => {
    const src = mapInstance.current && mapInstance.current.getSource(id);
    if (src) src.setData(fc || EMPTY_FC);
  };

  const setLayerVisible = (ids, visible) => {
    ids.forEach(id => {
      if (mapInstance.current && mapInstance.current.getLayer(id)) {
        mapInstance.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  };

  // ------------------------------------------------------------- popup HTML
  const createZipPopupContent = (p) => {
    const hispanicPercentage = p.total_population > 0 ? (p.hispanic_total / p.total_population) * 100 : 0;
    const countries = getTranslations('countries');
    const row = (flag, label, val) => (val > 0 ? `<div>${flag} ${label}: ${formatNumber(val)}</div>` : '');
    return `
      <div class="popup-content">
        <h6><strong>${tRef.current('popup.zipCode')} ${p.zipcode}</strong></h6>
        <div class="popup-stats">
          <div class="stat-row"><span class="stat-label">${tRef.current('popup.totalPopulation')}</span><span class="stat-value">${formatNumber(p.total_population)}</span></div>
          <div class="stat-row"><span class="stat-label">${tRef.current('popup.hispanics')}</span><span class="stat-value">${formatNumber(p.hispanic_total)} (${hispanicPercentage.toFixed(1)}%)</span></div>
        </div>
        <div class="popup-breakdown">
          <h6>${tRef.current('popup.breakdown')}</h6>
          <div class="country-list">
            ${row('🇲🇽', countries.mexicano, p.mexicano)}${row('🇵🇷', countries.puerto_rico, p.puerto_rico)}
            ${row('🇨🇺', countries.cubano, p.cubano)}${row('🇨🇴', countries.colombia, p.colombia)}
            ${row('🇩🇴', countries.dom_republic, p.dom_republic)}${row('🇬🇹', countries.guatemala, p.guatemala)}
            ${row('🇸🇻', countries.el_salvador, p.el_salvador)}${row('🇭🇳', countries.honduras, p.honduras)}
            ${row('🇪🇨', countries.ecuador, p.ecuador)}${row('🇵🇪', countries.peru, p.peru)}
            ${row('🇻🇪', countries.venezuela || 'Venezuela', p.venezuela)}${row('🇳🇮', 'Nicaragua', p.nicaragua)}
            ${row('🇵🇦', 'Panama', p.panama)}${row('🇨🇷', 'Costa Rica', p.costa_rica)}
            ${row('🇦🇷', 'Argentina', p.argentina)}${row('🇨🇱', 'Chile', p.chile)}
            ${row('🇧🇴', 'Bolivia', p.bolivia)}${row('🇵🇾', 'Paraguay', p.paraguay)}
            ${row('🇺🇾', 'Uruguay', p.uruguay)}${row('🇪🇸', countries.espana, p.espana)}
            ${row('🌎', 'Other Central American', p.otro_centro_america)}
            ${row('🌎', 'Other South American', p.otro_sudamerica)}
            ${row('🌎', 'Other Hispanic', p.otro_hispano)}
          </div>
        </div>
        ${p.estado ? `<div class="popup-location"><strong>${tRef.current('popup.estado')}</strong> ${p.estado}</div>` : ''}
        ${p.dma_code ? `<div class="popup-location"><strong>${tRef.current('popup.dma')}</strong> ${p.dma_code}</div>` : ''}
      </div>`;
  };

  const createTaPopupContent = (a, branchName) => {
    const rankBadge = a.hispanic_rank
      ? `<span class="ta-rank">#${Number(a.hispanic_rank).toLocaleString()}<small> / ${Number(a.rank_total || 0).toLocaleString()}</small></span>` : '';
    return `
      <div class="ta-popup">
        <span class="ta-cat">SRS Trade Area</span>
        <div class="ta-titlerow"><b>ZIP ${a.core_zip_code || ''}</b>${rankBadge}</div>
        ${branchName ? `<div class="ta-sub">${branchName}</div>` : ''}
        <div class="ta-data">
          <div class="ta-sdh">Hispanic rank · national${a.hispanic_rank ? ` — #${Number(a.hispanic_rank).toLocaleString()} of ${Number(a.rank_total || 0).toLocaleString()} areas` : ''}</div>
          <div class="ta-row"><span>% Hispanics trade area</span><b>${a.percent_hispanics_trade_area ?? 'N/A'}%</b></div>
          <div class="ta-row"><span>Hispanic ZIP population</span><b>${formatNumber(a.population_hispanic_zipcode)}</b></div>
          <div class="ta-row"><span>Total ZIP population</span><b>${formatNumber(a.population_zipcode)}</b></div>
          <div class="ta-row"><span>Hispanics trade area</span><b>${formatNumber(a.total_hispanics_trade_area)}</b></div>
          <div class="ta-row"><span>Total population trade area</span><b>${a.total_hispanics_trade_area && a.percent_hispanics_trade_area ? formatNumber(Math.round(parseFloat(a.total_hispanics_trade_area) / (parseFloat(a.percent_hispanics_trade_area) / 100))) : 'N/A'}</b></div>
        </div>
      </div>`;
  };

  const createBranchPanelContent = (p, tradeAreaData) => `
      <div class="popup-content">
        <h6><strong>${p.branch || p.dba || 'Branch'}</strong></h6>
        <div class="popup-stats">
          <div class="stat-row"><span class="stat-label">Distributor:</span><span class="stat-value">${p.distributor || 'N/A'}</span></div>
          <div class="stat-row"><span class="stat-label">DBA:</span><span class="stat-value">${p.dba || 'N/A'}</span></div>
          <div class="stat-row"><span class="stat-label">Address:</span><span class="stat-value">${p.address || 'N/A'}</span></div>
          <div class="stat-row"><span class="stat-label">City:</span><span class="stat-value">${p.city || 'N/A'}, ${p.state || ''} ${p.zip || ''}</span></div>
          ${p.phone ? `<div class="stat-row"><span class="stat-label">Phone:</span><span class="stat-value">${p.phone}</span></div>` : ''}
          ${tradeAreaData ? `
          <div class="stat-row"><span class="stat-label">% Hispanics Trade Area (${tradeAreaData.tradeAreaCount || 1} areas):</span><span class="stat-value">${tradeAreaData.percentHispanics}%</span></div>
          <div class="stat-row"><span class="stat-label">Total Hispanics Trade Area:</span><span class="stat-value">${formatNumber(parseInt(tradeAreaData.totalHispanics))}</span></div>
          <div class="stat-row"><span class="stat-label">Total Population Trade Area:</span><span class="stat-value">${formatNumber(parseInt(tradeAreaData.totalPopulation))}</span></div>
          ` : (p.hispanic_total ? `
          <div class="stat-row"><span class="stat-label">% Hispanics ZIP Code:</span><span class="stat-value">${p.total_population ? ((p.hispanic_total / p.total_population) * 100).toFixed(1) : 'N/A'}%</span></div>
          <div class="stat-row"><span class="stat-label">Total Hispanics ZIP Code:</span><span class="stat-value">${formatNumber(p.hispanic_total)}</span></div>
          <div class="stat-row"><span class="stat-label">Total Population ZIP Code:</span><span class="stat-value">${formatNumber(p.total_population)}</span></div>` : '')}
        </div>
        ${p.branch_website_url ? `<div class="popup-location"><a href="${p.branch_website_url}" target="_blank" rel="noopener noreferrer">View website</a></div>` : ''}
      </div>`;

  const createApolloPanelContent = (p) => {
    const colorLabel = APOLLO_COLOR_LABELS[p.color] || '';
    const colorBadge = p.color ? `<div class="stat-row"><span class="stat-label">Type:</span><span class="stat-value" style="font-weight: bold;">${colorLabel}</span></div>` : '';
    return `
      <div class="popup-content">
        <h6><strong>${p.company || 'Company'}</strong></h6>
        ${colorBadge}
        <div class="popup-stats">
          ${p.email ? `<div class="stat-row"><span class="stat-label">📧 Email:</span><span class="stat-value"><a href="mailto:${p.email}">${p.email}</a>${p.email_status ? ` <span style="font-size:10px;padding:1px 5px;border-radius:4px;color:#fff;background:${p.email_status === 'verified' ? '#28a745' : p.email_status === 'extrapolated' ? '#ff8c00' : '#6c757d'};">${p.email_status}</span>` : ''}</span></div>` : ''}
          ${p.company_name_for_emails ? `<div class="stat-row"><span class="stat-label">Company Name for Emails:</span><span class="stat-value">${p.company_name_for_emails}</span></div>` : ''}
          <div class="stat-row"><span class="stat-label">Industry:</span><span class="stat-value">${p.industry || 'N/A'}</span></div>
          <div class="stat-row"><span class="stat-label">Employees:</span><span class="stat-value">${p.employees_count || 'N/A'}</span></div>
          <div class="stat-row"><span class="stat-label">Stage:</span><span class="stat-value">${p.account_stage || 'N/A'}</span></div>
          ${p.account_owner ? `<div class="stat-row"><span class="stat-label">Account Owner:</span><span class="stat-value">${p.account_owner}</span></div>` : ''}
          <div class="stat-row"><span class="stat-label">Address:</span><span class="stat-value">${p.company_address || p.company_street || 'N/A'}</span></div>
          ${p.company_city && p.company_state ? `<div class="stat-row"><span class="stat-label">Location:</span><span class="stat-value">${p.company_city}, ${p.company_state} ${p.company_postal_code || ''}</span></div>` : ''}
          ${p.website ? `<div class="stat-row"><span class="stat-label">Website:</span><span class="stat-value"><a href="${p.website}" target="_blank" rel="noopener noreferrer">${p.domain || 'Visit'}</a></span></div>` : ''}
          ${p.company_phone ? `<div class="stat-row"><span class="stat-label">Phone:</span><span class="stat-value">${p.company_phone}</span></div>` : ''}
          ${p.annual_revenue ? `<div class="stat-row"><span class="stat-label">Annual Revenue:</span><span class="stat-value">${p.annual_revenue}</span></div>` : ''}
        </div>
      </div>`;
  };

  // ----------------------------------------------------------- interactions
  const showHoverPopup = (lngLat, html) => {
    if (!hoverPopupRef.current) {
      hoverPopupRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: 'gl-tooltip', offset: 10, maxWidth: '280px' });
    }
    hoverPopupRef.current.setLngLat(lngLat).setHTML(html).addTo(mapInstance.current);
  };
  const hideHoverPopup = () => { if (hoverPopupRef.current) hoverPopupRef.current.remove(); };

  const showClickPopup = (lngLat, html) => {
    if (clickPopupRef.current) clickPopupRef.current.remove();
    clickPopupRef.current = new maplibregl.Popup({ className: 'gl-popup', offset: 12, maxWidth: '340px' })
      .setLngLat(lngLat).setHTML(html).addTo(mapInstance.current);
  };

  const setHover = (sourceId, featureId) => {
    const prev = hoveredRef.current[sourceId];
    if (prev !== undefined && prev !== featureId) {
      mapInstance.current.setFeatureState({ source: sourceId, id: prev }, { hover: false });
    }
    if (featureId !== undefined) {
      mapInstance.current.setFeatureState({ source: sourceId, id: featureId }, { hover: true });
    }
    hoveredRef.current[sourceId] = featureId;
  };

  // ------------------------------------------------------------- map init
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapRef.current,
      style: POSITRON_GL_STYLE,
      center: US_CENTER,
      zoom: US_ZOOM_GL,
      attributionControl: { compact: true }
    });
    mapInstance.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

    map.on('load', () => {
      // ---- sources (todas parten vacías; los efectos las llenan) ----
      map.addSource('zips', { type: 'geojson', data: EMPTY_FC, promoteId: 'zipcode' });
      map.addSource('tradeareas', { type: 'geojson', data: EMPTY_FC, promoteId: 'id' });
      map.addSource('branches', { type: 'geojson', data: EMPTY_FC });
      map.addSource('apollo', { type: 'geojson', data: EMPTY_FC });
      map.addSource('ranking', { type: 'geojson', data: EMPTY_FC });
      map.addSource('qxotradeareas', { type: 'geojson', data: EMPTY_FC, promoteId: 'id' });
      map.addSource('qxobranches', { type: 'geojson', data: EMPTY_FC });

      // ---- capas (orden = z-order; los puntos SIEMPRE encima) ----
      map.addLayer({
        id: 'zips-fill', type: 'fill', source: 'zips',
        paint: {
          'fill-color': ['step', ['coalesce', ['*', ['/', ['get', 'hispanic_total'], ['max', ['get', 'total_population'], 1]], 100], 0],
            '#FFFF99', 10, '#FFC0CB', 20, '#FF6666', 30, '#8B0000'],
          'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.95, 0.75]
        }
      });
      map.addLayer({
        id: 'zips-line', type: 'line', source: 'zips',
        paint: {
          'line-color': ['case', ['boolean', ['feature-state', 'hover'], false], '#111827', '#ffffff'],
          'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1]
        }
      });
      map.addLayer({
        id: 'ta-fill', type: 'fill', source: 'tradeareas',
        paint: {
          'fill-color': ['step', ['coalesce', ['to-number', ['get', 'percent_hispanics_trade_area']], 0],
            '#c6dbef', 10, '#6baed6', 25, '#2171b5', 50, '#08306b'],
          'fill-opacity': ['case',
            ['boolean', ['feature-state', 'selected'], false], 0.88,
            ['boolean', ['feature-state', 'hover'], false], 0.8, 0.6]
        }
      });
      map.addLayer({
        id: 'ta-line', type: 'line', source: 'tradeareas',
        paint: {
          'line-color': ['case',
            ['boolean', ['feature-state', 'selected'], false], '#0f172a',
            ['boolean', ['feature-state', 'hover'], false], '#111827', '#ffffff'],
          'line-width': ['case',
            ['boolean', ['feature-state', 'selected'], false], 3.5,
            ['boolean', ['feature-state', 'hover'], false], 3, 1.5]
        }
      });
      // QXO trade areas (mismas escalas de azul)
      map.addLayer({
        id: 'qxota-fill', type: 'fill', source: 'qxotradeareas',
        paint: {
          'fill-color': ['step', ['coalesce', ['to-number', ['get', 'percent_hispanics_trade_area']], 0],
            '#c6dbef', 10, '#6baed6', 25, '#2171b5', 50, '#08306b'],
          'fill-opacity': 0.6
        }
      });
      map.addLayer({ id: 'qxota-line', type: 'line', source: 'qxotradeareas', paint: { 'line-color': '#ffffff', 'line-width': 1.5 } });
      // Contratistas: morados (Not Certified) debajo, certificados encima
      map.addLayer({
        id: 'apollo-morados', type: 'circle', source: 'apollo',
        filter: ['==', ['get', 'color'], 'morado'],
        paint: { 'circle-radius': 4.5, 'circle-color': '#6f42c1', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.2, 'circle-opacity': 0.8 }
      });
      map.addLayer({
        id: 'apollo-circles', type: 'circle', source: 'apollo',
        filter: ['!=', ['get', 'color'], 'morado'],
        paint: {
          'circle-radius': 5,
          'circle-color': APOLLO_COLOR_MATCH,
          'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5, 'circle-opacity': 0.85
        }
      });
      map.addLayer({
        id: 'qxo-circles', type: 'circle', source: 'qxobranches',
        paint: { 'circle-radius': 6, 'circle-color': '#22d3ee', 'circle-stroke-color': '#0a0f18', 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 }
      });
      map.addLayer({
        id: 'branches-circles', type: 'circle', source: 'branches',
        paint: {
          'circle-radius': 6,
          'circle-color': ['step', ['coalesce', ['*', ['/', ['get', 'hispanic_total'], ['max', ['get', 'total_population'], 1]], 100], 0],
            '#FFFF99', 10, '#FFC0CB', 25, '#FF6666', 50, '#8B0000'],
          'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5, 'circle-opacity': 0.9
        }
      });
      // Modo ranking Top-200: círculo + número
      map.addLayer({
        id: 'ranking-circles', type: 'circle', source: 'ranking',
        paint: {
          'circle-radius': 13,
          'circle-color': ['case', ['<=', ['get', 'ranking'], 10], '#ffd700', '#ff6b35'],
          'circle-stroke-color': '#0a0f18', 'circle-stroke-width': 2
        }
      });
      map.addLayer({
        id: 'ranking-labels', type: 'symbol', source: 'ranking',
        layout: { 'text-field': ['to-string', ['get', 'ranking']], 'text-size': 11, 'text-font': ['Montserrat Bold', 'Open Sans Bold', 'Noto Sans Bold'], 'text-allow-overlap': true },
        paint: { 'text-color': '#0a0f18' }
      });

      // ---- interacciones ----
      const cursorOn = ['zips-fill', 'ta-fill', 'qxota-fill', 'apollo-circles', 'apollo-morados', 'branches-circles', 'qxo-circles', 'ranking-circles'];
      cursorOn.forEach(id => {
        map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
      });

      // hover: trade areas (rank + zip) y zips (zip + %)
      map.on('mousemove', 'ta-fill', (e) => {
        const f = e.features[0];
        setHover('tradeareas', f.id);
        const p = f.properties;
        const rank = p.hispanic_rank ? `<b>#${Number(p.hispanic_rank).toLocaleString()}</b> · ` : '';
        showHoverPopup(e.lngLat, `${rank}Trade Area ZIP: ${p.core_zip_code}<br/>% Hispanics: ${p.percent_hispanics_trade_area}`);
      });
      map.on('mouseleave', 'ta-fill', () => { setHover('tradeareas', undefined); hideHoverPopup(); });

      map.on('mousemove', 'zips-fill', (e) => {
        // no pisar el tooltip de trade areas si están encima
        if (map.queryRenderedFeatures(e.point, { layers: ['ta-fill'] }).length) return;
        const f = e.features[0];
        setHover('zips', f.id);
        const p = f.properties;
        const pct = p.total_population > 0 ? ((p.hispanic_total / p.total_population) * 100).toFixed(1) : '0.0';
        showHoverPopup(e.lngLat, `<strong>${tRef.current('popup.zipCode')} ${p.zipcode}</strong><br>${tRef.current('popup.hispanics')} ${pct}%`);
      });
      map.on('mouseleave', 'zips-fill', () => { setHover('zips', undefined); hideHoverPopup(); });

      ['apollo-circles', 'apollo-morados'].forEach(lid => map.on('mouseleave', lid, hideHoverPopup));
      map.on('mousemove', 'apollo-morados', (e) => {
        const p = e.features[0].properties;
        showHoverPopup(e.lngLat, `<strong>${p.company || ''}</strong><br>${p.company_city || ''}, ${p.company_state || ''}<br>Not certified`);
      });
      map.on('mousemove', 'apollo-circles', (e) => {
        const p = e.features[0].properties;
        showHoverPopup(e.lngLat, `<strong>${p.company || ''}</strong><br>${p.company_city || ''}, ${p.company_state || ''}<br>${p.industry || 'N/A'}`);
      });
      map.on('mouseleave', 'apollo-circles', hideHoverPopup);

      map.on('mousemove', 'branches-circles', (e) => {
        const p = e.features[0].properties;
        showHoverPopup(e.lngLat, `<strong>${p.branch || p.dba || ''}</strong><br>${p.city || ''}, ${p.state || ''}`);
      });
      map.on('mouseleave', 'branches-circles', hideHoverPopup);

      // click: prioridad puntos > trade areas > zips (un solo handler global evita dobles popups)
      map.on('click', (e) => {
        hideHoverPopup(); // el tooltip no debe quedar montado sobre el popup
        const hits = map.queryRenderedFeatures(e.point, { layers: cursorOn.filter(id => map.getLayer(id)) });
        if (!hits.length) return;
        const f = hits[0];
        const p = f.properties;
        if (f.layer.id === 'apollo-circles' || f.layer.id === 'apollo-morados') {
          handleApolloClick(p);
        } else if (f.layer.id === 'branches-circles' || f.layer.id === 'qxo-circles' || f.layer.id === 'ranking-circles') {
          handleBranchClick(p);
        } else if (f.layer.id === 'ta-fill' || f.layer.id === 'qxota-fill') {
          if (selectedTaRef.current !== null) {
            map.setFeatureState({ source: 'tradeareas', id: selectedTaRef.current }, { selected: false });
          }
          selectedTaRef.current = f.id;
          map.setFeatureState({ source: 'tradeareas', id: f.id }, { selected: true });
          showClickPopup(e.lngLat, createTaPopupContent(p, p.__branchName));
        } else if (f.layer.id === 'zips-fill') {
          if (p.mexicano === undefined) {
            // feature de la carga nacional gruesa: traer el breakdown completo del zip
            showClickPopup(e.lngLat, `<div class="popup-content"><h6><strong>${tRef.current('popup.zipCode')} ${p.zipcode}</strong></h6><p>Loading…</p></div>`);
            fetchWithAuth(`${api.zipcodes()}?zipcode=${p.zipcode}&mode=polygons&limit=150`)
              .then(r => r.json())
              .then(d => {
                const full = d && d.features && d.features[0] && d.features[0].properties;
                if (full && clickPopupRef.current) clickPopupRef.current.setHTML(createZipPopupContent(full));
                else if (clickPopupRef.current) clickPopupRef.current.setHTML(createZipPopupContent(p));
              })
              .catch(() => { if (clickPopupRef.current) clickPopupRef.current.setHTML(createZipPopupContent(p)); });
          } else {
            showClickPopup(e.lngLat, createZipPopupContent(p));
          }
        }
      });

      // moveend → avisar a App (mismo contrato que Leaflet: {north,south,east,west}, zoom Leaflet-equivalente)
      let moveTimer;
      const onMove = () => {
        clearTimeout(moveTimer);
        moveTimer = setTimeout(() => {
          setZoomDisplay(leafletZoom());
          const z = leafletZoom();
          const b = boundsForApp();
          if (Math.abs(b.east - b.west) <= 180 && z >= 4) {
            onBoundsChangeRef.current(b, z);
          }
        }, 300);
      };
      map.on('moveend', onMove);
      map.on('zoomend', onMove);

      setZoomDisplay(leafletZoom());
      setMapReady(true);
    });

    return () => { try { map.remove(); } catch (e) { /* noop */ } mapInstance.current = null; };
  }, []);

  // ------------------------------------------------- clicks con fetch lazy
  const handleApolloClick = async (p) => {
    const subtitle = [p.company_city, p.company_state].filter(Boolean).join(', ');
    setDetailPanel({ title: p.company || 'Company', subtitle, loading: true, html: '' });
    try {
      const resp = await fetchWithAuth(api.apolloPointDetail(p.id));
      const full = resp.ok ? { ...(await resp.json()), type: 'apollo' } : p;
      setDetailPanel({
        title: full.company || 'Company',
        subtitle: [full.company_city, full.company_state].filter(Boolean).join(', ') || subtitle,
        html: createApolloPanelContent(full)
      });
    } catch (e) {
      setDetailPanel({ title: p.company || 'Company', subtitle, html: createApolloPanelContent(p) });
    }
  };

  const handleBranchClick = async (p) => {
    const branchTitle = p.branch || p.dba || 'Branch';
    const branchSubtitle = [p.city, p.state].filter(Boolean).join(', ');
    setDetailPanel({ title: branchTitle, subtitle: branchSubtitle, html: createBranchPanelContent(p, null) });
    try {
      let url = api.certainteedTradeAreas();
      const params = new URLSearchParams();
      if (p.place_id) params.append('place_id', p.place_id);
      else {
        if (p.zip) params.append('zip', p.zip);
        if (p.branch) params.append('branch', p.branch);
      }
      params.append('t', Date.now());
      url += '?' + params.toString();
      const response = await fetchWithAuth(url);
      const data = await response.json();
      if (data.trade_areas && data.trade_areas.length > 0) {
        const calc = data.trade_areas.map(a => {
          const population = parseFloat(a.population_zipcode) || 0;
          const pct = parseFloat(a.percent_hispanics_trade_area) || 0;
          return { population, hispanics: Math.round(population * (pct / 100)) };
        });
        const totalHispanics = calc.reduce((s, a) => s + a.hispanics, 0);
        const totalPopulation = calc.reduce((s, a) => s + a.population, 0);
        const percentHispanics = totalPopulation > 0 ? ((totalHispanics / totalPopulation) * 100).toFixed(2) : '0.00';
        setDetailPanel({
          title: branchTitle, subtitle: branchSubtitle,
          html: createBranchPanelContent(p, {
            percentHispanics, totalHispanics: String(totalHispanics), totalPopulation: String(totalPopulation),
            tradeAreaCount: data.trade_areas.length
          })
        });
      }
    } catch (e) { /* el panel ya muestra los datos base */ }
  };

  // --------------------------------------------------------- data effects
  // ZIPs nivel 1 — COBERTURA NACIONAL COMPLETA: al activar densityZip se cargan los
  // ~26,832 zips del país de un jalón en calidad gruesa (endpoint cacheado, ~2 MB gzip).
  // La GPU los pinta todos sin despeinarse; ya no hay "solo el top del viewport".
  const zipsAllLoadedRef = useRef(false);
  useEffect(() => {
    if (!mapReady || isTopHispanicActive || !layerVisibility.densityZip || zipsAllLoadedRef.current) return;
    zipsAllLoadedRef.current = true;
    (async () => {
      try {
        addToast('Loading all ZIP areas…', 'info');
        const response = await fetchWithAuth(api.zipcodesAll());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const geo = await response.json();
        (geo.features || []).forEach(f => {
          const zip = f.properties && f.properties.zipcode;
          if (zip && !zipFeaturesRef.current.has(zip)) zipFeaturesRef.current.set(zip, f);
        });
        setSourceData('zips', { type: 'FeatureCollection', features: Array.from(zipFeaturesRef.current.values()) });
        addToast(`${(geo.features || []).length.toLocaleString()} ZIP areas (full country)`, 'success');
      } catch (e) {
        console.error('Error cargando zipcodes-all:', e);
        zipsAllLoadedRef.current = false; // permitir reintento
        onBoundsChangeRef.current(boundsForApp(), leafletZoom()); // fallback: viewport clásico
      }
    })();
  }, [layerVisibility.densityZip, mapReady, isTopHispanicActive]);

  // ZIPs nivel 2 — refinamiento HD del viewport: el prop `data` (pipeline de App con
  // geometría fina + breakdown por país) REEMPLAZA la versión gruesa de esos zips.
  useEffect(() => {
    if (!mapReady) return;
    if (Array.isArray(data) && data.length > 0) {
      data.forEach(f => {
        const zip = f.properties && f.properties.zipcode;
        if (zip) zipFeaturesRef.current.set(zip, f); // HD pisa a la gruesa
      });
      setSourceData('zips', { type: 'FeatureCollection', features: Array.from(zipFeaturesRef.current.values()) });
    }
  }, [data, mapReady]);

  // visibilidad de capas (con la exclusividad trade area ↔ density del diseño original)
  useEffect(() => {
    if (!mapReady) return;
    const taOn = isTopHispanicActive || layerVisibility.certainteedTradeArea;
    const zipsOn = !isTopHispanicActive && layerVisibility.densityZip && !layerVisibility.certainteedTradeArea;
    setLayerVisible(['ta-fill', 'ta-line'], taOn);
    setLayerVisible(['zips-fill', 'zips-line'], zipsOn);
    setLayerVisible(['apollo-circles'], layerVisibility.apollo);
    setLayerVisible(['apollo-morados'], !!layerVisibility.notCertifiedContractors);
    setLayerVisible(['branches-circles'], !isTopHispanicActive && layerVisibility.certainteed);
    setLayerVisible(['qxo-circles'], !!layerVisibility.qxo);
    setLayerVisible(['qxota-fill', 'qxota-line'], !!layerVisibility.qxoTradeArea);
    setLayerVisible(['ranking-circles', 'ranking-labels'], isTopHispanicActive);
  }, [layerVisibility, mapReady, isTopHispanicActive]);

  // Filtro por tipo de certificación (checkboxes "Filter by Type" del Sidebar).
  //
  // Semántica heredada del componente Leaflet: se oculta SOLO lo explícitamente
  // desmarcado (contractorFilters[color] === false). Un color presente en los datos
  // pero sin checkbox en el panel — 'azul' (Certified) es el caso real — no tiene
  // entrada en contractorFilters y debe seguir visible. Por eso el filtro se arma
  // como lista negra y no como lista blanca: con una whitelist esos puntos
  // desaparecerían del mapa.
  useEffect(() => {
    if (!mapReady || !mapInstance.current) return;
    const map = mapInstance.current;
    if (!map.getLayer('apollo-circles')) return;

    const notMorado = ['!=', ['get', 'color'], 'morado'];
    const hidden = contractorFilters
      ? Object.keys(contractorFilters).filter(color => contractorFilters[color] === false)
      : [];

    map.setFilter(
      'apollo-circles',
      hidden.length
        ? ['all', notMorado, ['!', ['in', ['get', 'color'], ['literal', hidden]]]]
        : notMorado
    );
  }, [contractorFilters, mapReady]);

  // Branches: cargar cuando cambien filtros
  useEffect(() => {
    if (!mapReady || isTopHispanicActive) return;
    (async () => {
      let filtro = hispanicConcentration === 'Med' ? 'Medium' : hispanicConcentration;
      let url = `${api.certainteedPoints()}?limit=500`;
      if (filtro) url += `&hispanic_concentration=${encodeURIComponent(filtro)}`;
      if (filters.state) url += `&state=${encodeURIComponent(stateToAbbreviation[filters.state] || filters.state)}`;
      if (filters.certainteedBranch) url += `&branch=${encodeURIComponent(filters.certainteedBranch)}`;
      try {
        const response = await fetchWithAuth(url);
        if (!response.ok) throw new Error('Request error: ' + response.status);
        const geo = await response.json();
        setCertainteedPoints(geo);
        await dataCache.setCertainteedPoints({}, geo);
      } catch (e) { console.error('Error al cargar sucursales:', e); }
    })();
  }, [filters.state, filters.certainteedBranch, hispanicConcentration, mapReady, isTopHispanicActive]);

  // Branches → source (aplicando el filtro de concentración client-side, como el original)
  useEffect(() => {
    if (!mapReady || !certainteedPoints) return;
    let features = certainteedPoints.features || [];
    const filtro = hispanicConcentration === 'Med' ? 'Medium' : hispanicConcentration;
    if (filtro) {
      features = features.filter(f => (f.properties.hispanic_concentration || '').toLowerCase() === filtro.toLowerCase());
    }
    setSourceData('branches', { type: 'FeatureCollection', features });
  }, [certainteedPoints, hispanicConcentration, mapReady]);

  // Apollo: cargar cuando la capa esté activa o cambien filtros
  useEffect(() => {
    if (!mapReady || (!layerVisibility.apollo && !layerVisibility.notCertifiedContractors)) return;
    (async () => {
      const cached = await dataCache.getApolloPoints(filters);
      if (cached && !cached.error) { setApolloPoints(cached); return; }
      const params = new URLSearchParams();
      params.append('limit', '30000'); // certainteed: 23k+ contratistas incl. morados
      params.append('fields', 'light');
      if (layerVisibility.notCertifiedContractors) params.append('include_morados', 'true');
      if (filters.state) params.append('state', filters.state);
      // city puede venir como array (multi-seleccion) o string (compatibilidad)
      const cityParam = Array.isArray(filters.city) ? filters.city.join(',') : filters.city;
      if (cityParam) params.append('city', cityParam);
      if (filters.dba) params.append('company', filters.dba);
      try {
        const response = await fetchWithAuth(`${api.apolloPoints()}?${params.toString()}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const geo = await response.json();
        setApolloPoints(geo);
        const n = geo?.features?.length || 0;
        if (n > 0) addToast(`${n.toLocaleString()} CRM Prospects`, 'success');
        await dataCache.setApolloPoints(geo, filters);
      } catch (e) {
        console.error('Error cargando Apollo:', e);
        setApolloPoints(EMPTY_FC);
      }
    })();
  }, [layerVisibility.apollo, layerVisibility.notCertifiedContractors, filters.state, cityKey(filters.city), filters.dba, filters.certainteedBranch, mapReady]);

  useEffect(() => {
    if (!mapReady || !apolloPoints) return;
    setSourceData('apollo', apolloPoints);
  }, [apolloPoints, mapReady]);

  // QXO: cargar al activar (lazy)
  const qxoLoadedRef = useRef({ points: false, areas: false });
  useEffect(() => {
    if (!mapReady || !layerVisibility.qxo || qxoLoadedRef.current.points) return;
    qxoLoadedRef.current.points = true;
    (async () => {
      try {
        const r = await fetchWithAuth(`${api.qxoPoints()}?limit=1000`);
        const geo = await r.json();
        setSourceData('qxobranches', geo && geo.features ? geo : EMPTY_FC);
      } catch (e) { console.error('Error QXO points:', e); }
    })();
  }, [layerVisibility.qxo, mapReady]);
  useEffect(() => {
    if (!mapReady || !layerVisibility.qxoTradeArea || qxoLoadedRef.current.areas) return;
    qxoLoadedRef.current.areas = true;
    (async () => {
      try {
        const r = await fetchWithAuth(`${api.qxoTradeAreas()}?limit=3000`);
        const payload = await r.json();
        const areas = payload.trade_areas || [];
        const features = areas.filter(a => a.geometry).map(a => ({
          type: 'Feature',
          geometry: typeof a.geometry === 'string' ? JSON.parse(a.geometry) : a.geometry,
          properties: { id: a.id, core_zip_code: a.core_zip_code, percent_hispanics_trade_area: a.percent_hispanics_trade_area,
            population_zipcode: a.population_zipcode, population_hispanic_zipcode: a.population_hispanic_zipcode,
            total_hispanics_trade_area: a.total_hispanics_trade_area, hispanic_rank: a.hispanic_rank, rank_total: a.rank_total }
        }));
        setSourceData('qxotradeareas', { type: 'FeatureCollection', features });
        if (features.length) addToast(`${features.length.toLocaleString()} QXO trade areas`, 'success');
      } catch (e) { console.error('Error QXO areas:', e); }
    })();
  }, [layerVisibility.qxoTradeArea, mapReady]);

  // Trade areas: cargar según filtros/visibilidad (misma API + top200 que el original)
  useEffect(() => {
    if (!mapReady) return;
    if (!layerVisibility.certainteedTradeArea && !isTopHispanicActive) {
      setSourceData('tradeareas', EMPTY_FC);
      setTradeAreaCount(0);
      return;
    }
    (async () => {
      try {
        onTradeAreaLoadingChange && onTradeAreaLoadingChange(true);
        let url = api.certainteedTradeAreas();
        const params = new URLSearchParams();
        if (filters.state) params.append('state', filters.state);
        if (filters.certainteedBranch) params.append('branch', filters.certainteedBranch);
        if ((!filters.state && !filters.certainteedBranch) || isTopHispanicActive) params.append('top200', 'true');
        if (params.toString()) url += '?' + params.toString();
        const response = await fetchWithAuth(url);
        const payload = await response.json();
        const areas = payload.trade_areas || [];
        // nombre de sucursal para el popup (igual que el original: points del payload por place_id)
        const byPlace = {};
        (payload.points || []).forEach(pt => { if (pt.place_id) byPlace[pt.place_id] = pt.nombre || pt.dba; });
        const features = areas.filter(a => a.geometry).map(a => ({
          type: 'Feature',
          geometry: typeof a.geometry === 'string' ? JSON.parse(a.geometry) : a.geometry,
          properties: {
            id: a.id, core_zip_code: a.core_zip_code, place_id: a.place_id,
            population_zipcode: a.population_zipcode, population_hispanic_zipcode: a.population_hispanic_zipcode,
            percent_hispanics_trade_area: a.percent_hispanics_trade_area, total_hispanics_trade_area: a.total_hispanics_trade_area,
            hispanic_rank: a.hispanic_rank, rank_total: a.rank_total,
            __branchName: a.nombre || a.dba || byPlace[a.place_id] || ''
          }
        }));
        selectedTaRef.current = null;
        setSourceData('tradeareas', { type: 'FeatureCollection', features });
        setTradeAreaCount(features.length);
        if (features.length > 0) addToast(`${features.length.toLocaleString()} trade areas`, 'success');
      } catch (e) {
        console.error('Error cargando trade areas:', e);
      } finally {
        onTradeAreaLoadingChange && onTradeAreaLoadingChange(false);
      }
    })();
  }, [filters.state, filters.certainteedBranch, layerVisibility.certainteedTradeArea, isTopHispanicActive, mapReady]);

  // Modo ranking: pintar los markers numerados desde certainteedPoints del ranking
  useEffect(() => {
    if (!mapReady) return;
    if (!isTopHispanicActive) { setSourceData('ranking', EMPTY_FC); return; }
    const feats = (topHispanicTradeAreas || []).length && topHispanicTradeAreas[0].geometry
      ? topHispanicTradeAreas
      : (certainteedPoints?.features || []);
    const ranked = feats.map((f, i) => ({
      type: 'Feature', geometry: f.geometry,
      properties: { ...f.properties, ranking: f.properties.ranking || i + 1 }
    })).filter(f => f.geometry && f.geometry.type === 'Point');
    setSourceData('ranking', { type: 'FeatureCollection', features: ranked });
  }, [isTopHispanicActive, topHispanicTradeAreas, certainteedPoints, mapReady]);

  // Centrado por estado (bounds de sus sucursales, como el original)
  useEffect(() => {
    if (!mapReady || !mapInstance.current) return;
    if (!filters.state) {
      mapInstance.current.flyTo({ center: US_CENTER, zoom: US_ZOOM_GL, duration: 800 });
      return;
    }
    (async () => {
      try {
        const ab = stateToAbbreviation[filters.state] || filters.state;
        const response = await fetchWithAuth(`${api.certainteedPoints()}?state=${encodeURIComponent(ab)}&limit=500`);
        const geo = await response.json();
        const pts = (geo.features || []).map(f => f.geometry && f.geometry.coordinates).filter(c => c && c.length === 2);
        if (!pts.length) return;
        let minLng = 180, minLat = 90, maxLng = -180, maxLat = -90;
        pts.forEach(([lng, lat]) => {
          minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
        });
        mapInstance.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, maxZoom: 11, duration: 900 });
      } catch (e) { console.error('Error centrando estado:', e); }
    })();
  }, [filters.state, mapReady]);

  // Centrado por branch específica
  useEffect(() => {
    if (!mapReady || !filters.certainteedBranch || !mapInstance.current) return;
    (async () => {
      try {
        const response = await fetchWithAuth(`${api.certainteedPoints()}?branch=${encodeURIComponent(filters.certainteedBranch)}&limit=10`);
        const geo = await response.json();
        const f = (geo.features || []).find(x => x.geometry && x.geometry.coordinates);
        if (f) mapInstance.current.flyTo({ center: f.geometry.coordinates, zoom: 8, duration: 900 });
      } catch (e) { console.error('Error centrando branch:', e); }
    })();
  }, [filters.certainteedBranch, mapReady]);

  // ---------------------------------------------------------------- render
  return (
    <div className="map-wrapper">
      <div ref={mapRef} className="map-container" />

      <div className="gl-zoom-chip">Zoom: {zoomDisplay}</div>

      {(loading || tradeAreaLoading) && (
        <div className="map-loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <span>{loading ? t('map.loadingData') : t('map.loadingTradeAreas', 'Loading trade areas...')}</span>
          </div>
        </div>
      )}

      {/* Panel lateral de detalle (empresa / branch) */}
      {detailPanel && (
        <div className="map-detail-panel">
          <div className="map-detail-header">
            <div>
              <div className="map-detail-title">{detailPanel.title}</div>
              {detailPanel.subtitle ? <div className="map-detail-sub">{detailPanel.subtitle}</div> : null}
            </div>
            <button className="map-detail-close" onClick={() => setDetailPanel(null)} aria-label="Close">×</button>
          </div>
          <div className="map-detail-body">
            {detailPanel.loading
              ? <div className="map-detail-loading"><i className="fas fa-spinner fa-spin"></i> Loading…</div>
              : <div dangerouslySetInnerHTML={{ __html: detailPanel.html }} />}
          </div>
        </div>
      )}

      {/* Map legend — color key per active layer */}
      <div className={`map-legend ${legendOpen ? '' : 'collapsed'} ${detailPanel ? 'shifted' : ''}`}>
        <div className="map-legend-header" onClick={() => setLegendOpen(o => !o)}>
          <span><i className="fas fa-layer-group"></i> Legend</span>
          <i className={`fas fa-chevron-${legendOpen ? 'down' : 'up'}`}></i>
        </div>
        {legendOpen && (
          <div className="map-legend-body">
            {(layerVisibility.certainteed || layerVisibility.densityZip || layerVisibility.zipcodes || isTopHispanicActive) && (
              <div className="legend-section">
                <div className="legend-title">Hispanic Concentration (%)</div>
                <div className="legend-gradbar" style={{ background: 'linear-gradient(to right, #FFFF99, #FFC0CB, #FF6666, #8B0000)' }}></div>
                <div className="legend-gradlabels"><span>0%</span><span>25%</span><span>50%+</span></div>
              </div>
            )}
            {layerVisibility.certainteedTradeArea && (
              <div className="legend-section">
                <div className="legend-title">SRS Trade Area (% Hispanic){tradeAreaCount ? ` · ${tradeAreaCount.toLocaleString()}` : ''}</div>
                <div className="legend-gradbar" style={{ background: 'linear-gradient(to right, #c6dbef, #6baed6, #2171b5, #08306b)' }}></div>
                <div className="legend-gradlabels"><span>0%</span><span>25%</span><span>50%+</span></div>
              </div>
            )}
            {layerVisibility.certainteed && (
              <div className="legend-section">
                <div className="legend-title">SRS Stores</div>
                <div className="legend-row"><span className="legend-dot" style={{ background: '#ff6b35' }}></span> Branch locations</div>
              </div>
            )}
            {layerVisibility.apollo && (
              <div className="legend-section">
                <div className="legend-title">CRM Prospects</div>
                <div className="legend-row"><span className="legend-dot" style={{ background: '#007bff' }}></span> Prospect locations</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapComponentGL;
